import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  documentId,
  type Unsubscribe,
  type DocumentData,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import { db, functions } from "./firebase";
import type {
  BackfillAdminStatsRequest,
  BackfillAdminStatsResponse,
} from "../types/requests";

export interface AdminStats {
  registeredUsers?: number;
  generationsTotal?: number;
  activatedUsers?: number;
  sumTimeToFirstGenMs?: number;
  jobsSucceeded?: number;
  jobsFailed?: number;
  downloadsTotal?: number;
  uploadsTotal?: number;
  regenerationsTotal?: number;
  subscriptionsActive?: number;
  subscriptionsTotal?: number;
  generationTypes?: Record<string, number>;
  roomTypeCounts?: Record<string, number>;
  resolutionCounts?: Record<string, number>;
  styleCounts?: {
    interior?: Record<string, number>;
    outdoor?: Record<string, number>;
  };
  updatedAt?: unknown;
}

export interface AdminFeedbackItem {
  id: string;
  email?: string | null;
  userId?: string | null;
  message?: string | null;
  jobId?: string | null;
  context?: string | null;
  device?: string | null;
  imageUrl?: string | null;
  imagePath?: string | null;
  screen?: {
    width?: number;
    height?: number;
    devicePixelRatio?: number;
  } | null;
  createdAt?: unknown;
}

export function subscribeAdminStats(
  onUpdate: (stats: AdminStats | null) => void,
): Unsubscribe {
  const ref = doc(db, "admin", "stats");
  return onSnapshot(ref, (snapshot) => {
    if (!snapshot.exists()) {
      onUpdate(null);
      return;
    }
    onUpdate(snapshot.data() as AdminStats);
  });
}

export function subscribeFeedback(
  onUpdate: (items: AdminFeedbackItem[]) => void,
): Unsubscribe {
  const ref = collection(db, "feedback");
  const q = query(ref, orderBy("createdAt", "desc"), limit(50));
  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...(docSnap.data() as Omit<AdminFeedbackItem, "id">),
    }));
    onUpdate(items);
  });
}

export async function fetchFeedbackPage(
  pageSize = 10,
  cursor?: QueryDocumentSnapshot<DocumentData> | null,
): Promise<{
  items: AdminFeedbackItem[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
}> {
  const ref = collection(db, "feedback");
  const baseQuery = query(ref, orderBy("createdAt", "desc"));
  const pagedQuery = cursor
    ? query(baseQuery, startAfter(cursor), limit(pageSize))
    : query(baseQuery, limit(pageSize));
  const snapshot = await getDocs(pagedQuery);
  const items = snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...(docSnap.data() as Omit<AdminFeedbackItem, "id">),
  }));
  const lastDoc =
    snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
  return { items, lastDoc };
}

export async function fetchAdminStatsRange(
  startDate: string,
  endDate: string,
): Promise<AdminStats> {
  const ref = collection(db, "admin_stats_daily");
  const q = query(
    ref,
    where(documentId(), ">=", startDate),
    where(documentId(), "<=", endDate),
  );
  const snap = await getDocs(q);
  const totals: AdminStats = {
    generationTypes: {},
    styleCounts: { interior: {}, outdoor: {} },
  };
  snap.forEach((docSnap) => {
    const data = docSnap.data();
    mergeAdminStats(totals, data);
  });
  return totals;
}

export async function backfillAdminStats(): Promise<BackfillAdminStatsResponse> {
  const callable = httpsCallable<
    BackfillAdminStatsRequest,
    BackfillAdminStatsResponse
  >(functions, "backfillAdminStats");
  const result = await callable({});
  return result.data;
}

function mergeAdminStats(target: AdminStats, source: Record<string, any>) {
  for (const [key, value] of Object.entries(source)) {
    if (key === "updatedAt" || key === "date") continue;
    if (typeof value === "number") {
      target[key as keyof AdminStats] =
        (target[key as keyof AdminStats] as number | undefined || 0) + value;
      continue;
    }
    if (value && typeof value === "object") {
      if (key === "generationTypes") {
        target.generationTypes = sumNested(
          target.generationTypes ?? {},
          value,
        );
      }
      if (key === "roomTypeCounts") {
        target.roomTypeCounts = sumNested(
          target.roomTypeCounts ?? {},
          value,
        );
      }
      if (key === "resolutionCounts") {
        target.resolutionCounts = sumNested(
          target.resolutionCounts ?? {},
          value,
        );
      }
      if (key === "styleCounts") {
        const interior = sumNested(
          target.styleCounts?.interior ?? {},
          value.interior ?? {},
        );
        const outdoor = sumNested(
          target.styleCounts?.outdoor ?? {},
          value.outdoor ?? {},
        );
        target.styleCounts = { interior, outdoor };
      }
    }
  }
}

function sumNested(
  current: Record<string, number>,
  incoming: Record<string, any>,
) {
  const result: Record<string, number> = { ...current };
  for (const [key, value] of Object.entries(incoming)) {
    if (typeof value !== "number") continue;
    result[key] = (result[key] ?? 0) + value;
  }
  return result;
}
